import type { ReactNode } from 'react';
import { Link } from '@backstage/core-components';
import { LinearProgress, makeStyles } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    color: 'inherit',
    textDecoration: 'none',
    minWidth: 0,
    borderRadius: 6,
    // The theme underlines every link on hover; here only the title is.
    '&:hover': { textDecoration: 'none' },
    '&:hover $title': { textDecoration: 'underline' },
  },
  icon: {
    flex: '0 0 auto',
    display: 'grid',
    placeItems: 'center',
    width: 32,
    height: 32,
    borderRadius: 6,
    border: '1px solid var(--ui-border)',
    color: 'var(--ui-text-muted)',
  },
  text: { flex: 1, minWidth: 0 },
  title: { fontWeight: 600 },
  sub: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  go: { flex: '0 0 auto', color: 'var(--ui-text-muted)', fontSize: 18 },
}));

export type RowProps = {
  icon: ReactNode;
  title: string;
  /** What the row says beneath its title; `loading` shows a placeholder. */
  sub?: ReactNode;
  loading?: boolean;
  to: string;
  /** Leaves the portal — marked so the reader knows before clicking. */
  external?: boolean;
};

/**
 * One row of an Overview column: a thing about the component, one line on its
 * state, and where following it leads — inside the portal (a chevron) or out of
 * it (the leave-the-portal sign).
 */
export const OverviewRow = ({
  icon,
  title,
  sub,
  loading,
  to,
  external,
}: RowProps) => {
  const classes = useStyles();
  return (
    <Link to={to} className={classes.row} underline="none">
      <span className={classes.icon} aria-hidden>
        {icon}
      </span>
      <span className={classes.text}>
        <div className={classes.title}>{title}</div>
        {loading ? (
          <LinearProgress
            aria-label={`Loading ${title}`}
            style={{ width: 90, height: 4, borderRadius: 2, marginTop: 6 }}
          />
        ) : (
          sub && <div className={classes.sub}>{sub}</div>
        )}
      </span>
      {external ? (
        <OpenInNewIcon className={classes.go} />
      ) : (
        <ChevronRightIcon className={classes.go} />
      )}
    </Link>
  );
};
